import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/ui';
import { agentApi, type LeadTimelineEvent, type LeadEventType } from '@/shared/api/agentApi';

/**
 * ציר הזמן של הליד: מה קרה לו, מתי, ומי עשה את זה.
 *
 * הסדר הוא מהחדש לישן — הנציג רוצה קודם כול לדעת מה קרה עכשיו. שורת הקלט
 * מקובעת בתחתית, ואחרי שליחה טוענים מחדש מהשרת במקום להוסיף שורה אופטימית,
 * כדי שהחותמת והשם יהיו של השרת ולא ניחוש של הדפדפן.
 */

/** אותה תצוגה כמו בהיסטוריית השיחות, כדי שהתאריכים ייראו זהים בכל המסכים. */
function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
}

const EVENT_ICON: Record<LeadEventType, string> = {
  note: 'chat',
  status_changed: 'flag',
  assigned: 'user',
  campaign_changed: 'target',
  call: 'phone',
  created: 'plus',
  followup_scheduled: 'clock',
};

const EVENT_COLOR: Record<LeadEventType, string> = {
  note: 'var(--muted)',
  status_changed: 'var(--accent)',
  assigned: 'var(--ok)',
  campaign_changed: 'var(--accent)',
  call: 'var(--info, var(--accent))',
  created: 'var(--muted)',
  followup_scheduled: 'var(--warn)',
};

export function LeadTimeline({ leadId }: { leadId: number }) {
  const [rows, setRows] = useState<LeadTimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    agentApi.leadTimeline(leadId)
      .then(setRows)
      .catch(() => setError('שגיאה בטעינת ההיסטוריה'))
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  async function send() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      await agentApi.addLeadNote(leadId, body);
      setDraft('');
      load();
    } catch {
      setError('ההערה לא נשמרה. נסו שוב.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>היסטוריה</div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingInlineEnd: 4 }}>
        {loading ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            אין עדיין היסטוריה לליד הזה.
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {/* הקו האנכי שעליו יושבים האירועים */}
            <div style={{
              position: 'absolute', insetInlineStart: 15, top: 6, bottom: 6,
              width: 1, background: 'var(--line)',
            }} />
            {rows.map((e) => (
              <div key={e.id} style={{ display: 'flex', gap: 10, position: 'relative', paddingBottom: 14 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--surface)', border: '1px solid var(--line)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                }}>
                  <Icon name={EVENT_ICON[e.type] ?? 'dot'} size={14} color={EVENT_COLOR[e.type] ?? 'var(--muted)'} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 'var(--t-sm)', fontWeight: 500 }}>{e.title}</span>
                    <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.created_at)}
                    </span>
                  </div>
                  {e.actor_name && (
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{e.actor_name}</div>
                  )}
                  {e.body && (
                    <div style={{
                      marginTop: 6, padding: '8px 10px', borderRadius: 'var(--r-2)',
                      background: 'var(--surface-2)', fontSize: 'var(--t-sm)',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {e.body}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', fontSize: 'var(--t-xs)', marginTop: 6 }}>{error}</div>
      )}

      {/* שורת הקלט המקובעת */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', marginTop: 10,
        borderTop: '1px solid var(--line)', paddingTop: 10,
      }}>
        <Icon name="bolt" size={16} color="var(--muted)" />
        <input
          className="input"
          style={{ flex: 1 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
          placeholder="הוספת הערה…"
          aria-label="הוספת הערה"
          disabled={sending}
        />
        <button
          type="button"
          className="btn"
          onClick={() => void send()}
          disabled={sending || !draft.trim()}
          aria-label="שליחה"
          title="שליחה"
        >
          <Icon name="send" size={16} />
        </button>
      </div>
    </div>
  );
}
